/**
 * Which template the designer should be on, after an answer changed.
 *
 * Two things can move a product to another template: a picked choice that
 * names one (Customily's "change template") and the page's variation matching
 * a variation rule (Customily's "change template by variant"). A choice wins
 * while it is picked. Without one, the designer belongs on the variation
 * rule's template, and only on the product's own template when no rule
 * matches. Returning to the product's own template while a variation rule
 * holds another one undid the variation: on a Mini Box whose six colours each
 * have a template, every colour click went back to the main template's
 * design, the server refused it with the other colour's questions, and the
 * shopper kept seeing the first colour.
 */
export type TemplateSwitch =
  | { kind: 'stay' }
  /** Load this template, which a picked choice names. */
  | { kind: 'choice'; templateId: string }
  /** Load what the variation calls for (its rule's template, or the product's own). */
  | { kind: 'variation' };

export function templateSwitchFor(input: {
  /** The template a picked choice names, if any. */
  choiceTemplateId?: string;
  /** The template the page's variation rule names, if a rule matches. */
  ruleTemplateId?: string;
  /** The product's own template. */
  baseTemplateId: string;
  /** The template the designer is showing. */
  currentTemplateId: string;
}): TemplateSwitch {
  if (input.choiceTemplateId) {
    return input.choiceTemplateId === input.currentTemplateId
      ? { kind: 'stay' }
      : { kind: 'choice', templateId: input.choiceTemplateId };
  }
  const wanted = input.ruleTemplateId || input.baseTemplateId;
  if (!wanted || wanted === input.currentTemplateId) return { kind: 'stay' };
  return { kind: 'variation' };
}
